import moment from 'moment';

export function toMoment(raw) {
    if (raw == null || raw === '')
        return null;
    try {
        const m = moment.isMoment(raw) && typeof raw.clone === 'function'
            ? raw.clone()
            : moment(raw);
        return m && m.isValid && m.isValid() ? m : null;
    } catch (_e) {
        return null;
    }
}

export function toDuration(raw) {
    if (raw == null)
        return null;
    try {
        const d = moment.duration(raw);
        if (d && typeof d.asMilliseconds === 'function' && d.asMilliseconds() > 0)
            return d;
    } catch (_e) {
        // ignore
    }
    return null;
}

/** When user leaves time at midnight on an "end" field, treat as end of calendar day (F7492). */
export function applyEndOfDayIfMidnight(m, enabled) {
    if (!enabled || !m || !m.isValid())
        return m;
    if (m.hours() === 0 && m.minutes() === 0 && m.seconds() === 0 && m.milliseconds() === 0)
        return m.clone().endOf('day');
    return m;
}

export function displayString(m, withTime) {
    if (!m || !m.isValid())
        return '';
    return withTime ? m.format('YYYY-MM-DDTHH:mm') : m.format('YYYY-MM-DD');
}

export function fillMissingTime(str, endOfDay) {
    const s = String(str || '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return `${s}T${endOfDay ? '23:59' : '00:00'}`;
    }
    if (/^\d{4}-\d{2}-\d{2}T$/.test(s)) {
        const d = s.slice(0, 10);
        return `${d}T${endOfDay ? '23:59' : '00:00'}`;
    }
    if (/^\d{4}-\d{2}-\d{2}T--:--$/.test(s)) {
        const d = s.slice(0, 10);
        return `${d}T${endOfDay ? '23:59' : '00:00'}`;
    }
    return null;
}

export function parseInput(str, withTime) {
    if (!str || String(str).trim() === '')
        return null;
    if (withTime) {
        const filled = fillMissingTime(str, false);
        if (filled) {
            const mf = moment(filled, 'YYYY-MM-DDTHH:mm', true);
            return mf.isValid() ? mf : null;
        }
    }
    let m = withTime
        ? moment(str, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true)
        : moment(str, 'YYYY-MM-DD', true);
    if (!m.isValid())
        m = moment(str);
    return m.isValid() ? m : null;
}

/** True when an end field should snap to 23:59 on a calendar-driven change (not spinners). */
export function shouldSnapEndOfDayOnCalendarPick(parsed, committed, endOfDayIfMidnight) {
    if (!endOfDayIfMidnight || !parsed?.isValid())
        return false;
    if (parsed.hours() === 0 && parsed.minutes() === 0)
        return false;
    if (!committed?.isValid())
        return true;
    if (!parsed.isSame(committed, 'day'))
        return true;
    return false;
}

/**
 * End-field EOD snap for an immediate `userPicked` commit. Same-calendar-day
 * hour/minute edits (native spinners) keep the chosen time; a new day from the
 * calendar popover still snaps to 23:59.
 */
export function shouldSnapEndOfDayOnUserPick(
    parsed, committed, endOfDayIfMidnight, currentDisplay, str, withTime,
) {
    if (!shouldSnapEndOfDayOnCalendarPick(parsed, committed, endOfDayIfMidnight))
        return false;
    if (!withTime || !currentDisplay || !str)
        return true;
    const shown = parseInput(currentDisplay, true);
    if (shown?.isValid() && parsed.isValid() && shown.isSame(parsed, 'day'))
        return false;
    return true;
}

/**
 * Same pipeline as a real commit, for equality checks and blur/change.
 * @returns {{ kind: 'empty' } | { kind: 'invalidParse' } | { kind: 'invalidRule' } | { kind: 'ok', outMoment: object }}
 */
export function buildOutMomentFromDraft(draft, {
    withTime, endOfDayIfMidnight, onBlur, fromCalendar, isValidDate,
}) {
    if (draft === '' || draft == null)
        return { kind: 'empty' };
    const maybeFilled = withTime ? fillMissingTime(draft, Boolean(endOfDayIfMidnight)) : null;
    const m = parseInput(maybeFilled || draft, withTime);
    if (!m)
        return { kind: 'invalidParse' };
    const candidate = withTime ? m.clone() : m.clone().startOf('day');
    if (typeof isValidDate === 'function' && !isValidDate(candidate))
        return { kind: 'invalidRule' };
    let outMoment = m.clone();
    if (withTime && endOfDayIfMidnight && fromCalendar)
        outMoment = m.clone().endOf('day');
    else if (withTime && endOfDayIfMidnight && onBlur && maybeFilled) {
        if (m.hours() === 0 && m.minutes() === 0)
            outMoment = applyEndOfDayIfMidnight(outMoment, true);
        else
            outMoment = m.clone().endOf('day');
    }
    else if (!withTime && endOfDayIfMidnight)
        outMoment = m.clone().endOf('day');
    else if (!withTime)
        outMoment = m.clone().startOf('day');
    return { kind: 'ok', outMoment };
}

export function isCompleteNativeValue(str, withTime) {
    if (str == null)
        return false;
    const s = String(str);
    const wantLen = withTime ? 16 : 10;
    return s.length >= wantLen;
}

export const dtpDebugEnabled = () => (
    typeof localStorage !== 'undefined'
    && localStorage.getItem('DEBUG_DTP') === '1'
);

export function dtpLog(...args) {
    if (dtpDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.log('[DateTimePicker]', ...args);
    }
}

export function isTypingInputEvent(e) {
    const it = e?.nativeEvent?.inputType;
    if (!it)
        return false;
    return (
        it === 'insertText'
        || it === 'insertCompositionText'
        || it === 'deleteContentBackward'
        || it === 'deleteContentForward'
        || it === 'deleteByCut'
        || it === 'insertFromPaste'
        || it === 'insertFromDrop'
    );
}

/** True for native calendar day change or hour/minute spinner steps (±1), not segment typing. */
export function looksLikeNativeStepOrDayChange(fromM, toM, withTime) {
    if (!fromM?.isValid() || !toM?.isValid())
        return false;
    if (!withTime)
        return !fromM.isSame(toM, 'day');
    if (!fromM.isSame(toM, 'day'))
        return true;
    const minDiff = Math.abs(toM.minute() - fromM.minute());
    const hourDiff = Math.abs(toM.hour() - fromM.hour());
    if (hourDiff === 0 && (minDiff === 1 || minDiff === 59))
        return true;
    if (minDiff === 0 && (hourDiff === 1 || hourDiff === 23))
        return true;
    return false;
}

/**
 * Calendar popover or native steppers (hour/minute): commit when the value is
 * complete and differs from the store, even if the browser reports `insertText`
 * (Chrome datetime-local spinners).
 *
 * @param {object} [openAtHint] moment-like `openAt` hint — when the store is
 *   empty, ignore implicit browser values that match this hint on focus/click.
 * @param {string} [currentDisplay] native `datetime-local` string currently
 *   shown in the field (draft / store / preview). Spinner 23→22→23 must compare
 *   against what the user sees, not only the ISO minute in redux-form: e.g.
 *   store `21:59Z` and native `2026-08-14T23:59` share a minute but differ on
 *   screen after an intermediate `22:59`.
 */
export function isUserPickedChange(
    e, str, withTime, committedRaw, openAtHint = null, currentDisplay = '',
    recentTyping = false,
) {
    if (str === '' || !isCompleteNativeValue(str, withTime))
        return false;
    if (!withTime && recentTyping)
        return false;
    const parsed = parseInput(str, withTime);
    if (!parsed?.isValid())
        return false;
    const hint = toMoment(openAtHint);
    const committed = toMoment(committedRaw);
    const unit = withTime ? 'minute' : 'day';

    // Same-day spinner round-trip (23→21→23): compare to what is on screen. On an empty
    // store, a spinner value that differs from the on-screen preview is still a user pick.
    // Native datetime-local segment typing (30→01 while entering "10") is not a ±1 step.
    if (currentDisplay && str !== currentDisplay) {
        if (committed?.isValid() && parsed?.isValid()) {
            if (parsed.isSame(committed, unit))
                return true;
            if (!looksLikeNativeStepOrDayChange(committed, parsed, withTime))
                return false;
        }
        if (recentTyping && isTypingInputEvent(e))
            return false;
        return true;
    }

    if (committed?.isValid()) {
        if (parsed.isSame(committed, unit))
            return false;
        if (!looksLikeNativeStepOrDayChange(committed, parsed, withTime))
            return false;
        return true;
    }
    if (isTypingInputEvent(e))
        return false;
    if (hint?.isValid() && parsed.isSame(hint, unit))
        return false;
    return true;
}

/** True if the user (or the browser) had recent text-like activity on the field. */
export function isWithinRecentInputActivity(nowMs, lastKeyboard, lastBefore, lastInput, windowMs = 1500) {
    return (nowMs - lastKeyboard) < windowMs
        || (nowMs - lastBefore) < windowMs
        || (nowMs - lastInput) < windowMs;
}

/**
 * Keyboard / `beforeinput`–style activity only. The native date/datetime popover
 * typically fires `input` on the field before `change` without a preceding
 * keydown, so if we also treat "recent `input`" as editing, a calendar pick
 * is mis-classified and we do not commit to redux until blur (no search).
 */
export function isWithinRecentTypingProbes(
    nowMs, lastKeyboard, lastBefore, windowMs = 1500,
) {
    return (nowMs - lastKeyboard) < windowMs
        || (nowMs - lastBefore) < windowMs;
}
